import React, { useState } from 'react';
import MedicalServicesRoundedIcon from '@mui/icons-material/MedicalServicesRounded';
import HealingRoundedIcon from '@mui/icons-material/HealingRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import { Sprite } from '../Sprites/Sprite';
import './MultiClaimCI.scss';

/**
 * 「多重賠償危疾保障」, a 理財概念 chart in the look of ai.portfoplus.com. Having cancer is not
 * the same as dying of it: the first part shows, out of every 100 people diagnosed, how many
 * are alive five years on, for all cancers and for a few common ones. The second part follows
 * a patient from diagnosis to years after recovery and compares a plan that pays once with one
 * that can pay again. The survival figures are the Hong Kong Cancer Registry's (5-year relative
 * survival of adults diagnosed in 2018-2022); how a plan pays again is stated generally, as
 * every plan's terms differ.
 */

/** 5-year relative survival, %, adults diagnosed 2018-2022 (Overview of Hong Kong Cancer Statistics of 2023). */
const SURVIVAL = [
  { key: 'all', percent: 55.7 },
  { key: 'thyroid', percent: 92.4 },
  { key: 'breast', percent: 86.0 },
  { key: 'prostate', percent: 84.2 },
  { key: 'pancreas', percent: 13.2 }
];

type Plan = 'single' | 'multi';
type Status = 'paid' | 'ended' | 'covered' | 'again';

const STEPS = [
  { key: 1, Icon: MedicalServicesRoundedIcon },
  { key: 2, Icon: HealingRoundedIcon },
  { key: 3, Icon: FavoriteRoundedIcon },
  { key: 4, Icon: AutorenewRoundedIcon }
];

/** What each plan does at each step, in the order of STEPS. */
const STATUS: Record<Plan, Status[]> = {
  single: ['paid', 'ended', 'ended', 'ended'],
  multi: ['paid', 'covered', 'covered', 'again']
};

const OVERVIEW: Record<string, string> = {
  en: 'https://www3.ha.org.hk/cancereg/pdf/overview/Overview%20of%20HK%20Cancer%20Stat%202023.pdf'
};
const OVERVIEW_TC = 'https://www3.ha.org.hk/cancereg/pdf/overview/Overview%20of%20HK%20Cancer%20Stat%202023_tc.pdf';

/** The gauge's half-ring, left end to right end over the top. */
const GAUGE_ARC = 'M20,100 A80,80 0 0 1 180,100';

/** How many of the hundred are alive: the nearest whole person to the registry's percentage. */
export const alive = (percent: number) => Math.round(percent);

interface Intl {
  locale: string;
  formatMessage: (descriptor: { id: string }, values?: Record<string, string | number>) => string;
}

/** The app's `zh` is Simplified Chinese; `lang` picks the CJK font in the stylesheet. */
const HTML_LANG: Record<string, string> = { zh: 'zh-CN', 'zh-Hant-HK': 'zh-HK' };

const MultiClaimCI = ({ intl }: { intl: Intl }) => {
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const [cancer, setCancer] = useState(SURVIVAL[0].key);
  const [plan, setPlan] = useState<Plan>('single');
  // The last step, where the two plans differ most.
  const [step, setStep] = useState(4);

  const picked = SURVIVAL.find(item => item.key === cancer) || SURVIVAL[0];
  const living = alive(picked.percent);
  const statuses = STATUS[plan];
  const detailId = (n: number) => (n === 1 ? 'mc-d-1' : `mc-d-${n}-${plan}`);

  return (
    <div className="multi-claim-ci" lang={HTML_LANG[intl.locale] || intl.locale}>
      <header className="mc-head">
        <h2 className="mc-title">
          <span>{t('mc-title')}</span>
        </h2>
        <p className="mc-lead">{t('mc-lead')}</p>
      </header>

      <section className="mc-block is-alive" aria-live="polite">
        <h3>{t('mc-s1-head')}</h3>
        <p className="mc-sub">{t('mc-s1-sub')}</p>
        {/* A half-ring filled to the share who are alive, the figure in its middle. */}
        <div className="mc-gauge" role="img" aria-label={t('mc-alive', { n: living })}>
          <svg viewBox="0 0 200 108" aria-hidden="true">
            <path className="mc-gauge-track" d={GAUGE_ARC} pathLength={100} />
            <path className="mc-gauge-fill" d={GAUGE_ARC} pathLength={100} strokeDasharray={`${picked.percent} 100`} />
          </svg>
          <strong>{picked.percent}%</strong>
        </div>
        <p className="mc-alive">{t('mc-alive', { n: living })}</p>
        {/* All five at once; each row is also the way to pick it. */}
        <ul className="mc-bars" aria-label={t('mc-s1-head')}>
          {SURVIVAL.map(({ key, percent }) => (
            <li key={key}>
              <button
                type="button"
                className={key === cancer ? 'is-on' : ''}
                aria-pressed={key === cancer}
                onClick={() => setCancer(key)}
              >
                <span className="mc-bar-name">{t(`mc-c-${key}`)}</span>
                <span className="mc-bar-track">
                  <span className="mc-bar-fill" style={{ width: `${percent}%` }} />
                </span>
                <span className="mc-bar-value">{percent}%</span>
              </button>
            </li>
          ))}
        </ul>
        <small>{t('mc-alive-note')}</small>
      </section>

      <section className="mc-block is-journey" aria-live="polite">
        <h3>{t('mc-s2-head')}</h3>
        <p className="mc-sub">{t('mc-s2-sub')}</p>
        <div className="mc-plans" role="group" aria-label={t('mc-s2-head')}>
          {(['single', 'multi'] as Plan[]).map(p => (
            <button
              key={p}
              type="button"
              className={p === plan ? `is-on is-${p}` : ''}
              aria-pressed={p === plan}
              onClick={() => setPlan(p)}
            >
              {t(`mc-plan-${p}`)}
            </button>
          ))}
        </div>
        <ol className={`mc-journey is-${plan}`}>
          {STEPS.map(({ key, Icon }, i) => (
            <li key={key} className={`is-${statuses[i]}${key === step ? ' is-picked' : ''}`}>
              <button type="button" aria-pressed={key === step} onClick={() => setStep(key)}>
                <span className="mc-node" aria-hidden="true">
                  <Icon />
                </span>
                <strong>{t(`mc-step-${key}`)}</strong>
                <em>{t(`mc-status-${statuses[i]}`)}</em>
              </button>
            </li>
          ))}
        </ol>
        <div className={`mc-detail is-${statuses[step - 1]}`}>
          {/* The shield smiles while the plan covers you, and cracks once it has ended. */}
          <Sprite
            name={statuses[step - 1] === 'ended' ? 'shield-cracked' : 'shield-happy'}
            size={60}
            bob={statuses[step - 1] !== 'ended'}
          />
          <p>{t(detailId(step))}</p>
        </div>
      </section>

      <div className="mc-tip">
        <VerifiedUserRoundedIcon aria-hidden="true" />
        <p>{t('mc-tip')}</p>
      </div>

      <p className="mc-disclaimer">
        {t('dsc-disclaimer-1')}
        <br />
        {t('dsc-disclaimer-2')}
      </p>
      <p className="mc-source">
        <strong>{t('mc-source')}</strong>
        <a href={OVERVIEW[intl.locale] || OVERVIEW_TC} target="_blank" rel="noopener noreferrer">
          {t('mc-source-1')}
        </a>
      </p>
    </div>
  );
};

export default MultiClaimCI;
